/**
 * Browser-side backup of an in-progress case sheet, so edits survive a refresh,
 * a network drop or an HMIS outage before they have reached the DB.
 *
 * The backup is written on every edit and removed as soon as the DB confirms it
 * has the latest edits (or the record is saved), so a backup that exists on open
 * always means "unsynced edits". It is keyed by user + encounter and expires, since
 * it holds patient data in the browser.
 */
const TTL_MS = 48 * 60 * 60 * 1000;

const storageKey = (userId: string, encounterId: string) =>
  `caseSheetBackup:${userId}:${encounterId}`;

export function writeCaseSheetBackup(userId: string, encounterId: string, fields: unknown): void {
  try {
    localStorage.setItem(
      storageKey(userId, encounterId),
      JSON.stringify({ savedAt: Date.now(), fields })
    );
  } catch {
    // Storage full/blocked (private mode) — the DB autosave is still the primary copy.
  }
}

export function readCaseSheetBackup<T>(userId: string, encounterId: string): T | null {
  try {
    const raw = localStorage.getItem(storageKey(userId, encounterId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { savedAt?: number; fields?: T };
    if (!parsed.fields || !parsed.savedAt || Date.now() - parsed.savedAt > TTL_MS) {
      clearCaseSheetBackup(userId, encounterId);
      return null;
    }
    return parsed.fields;
  } catch {
    return null;
  }
}

export function clearCaseSheetBackup(userId: string, encounterId: string): void {
  try {
    localStorage.removeItem(storageKey(userId, encounterId));
  } catch {
    // ignore
  }
}
